import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getEnseignes, 
  createEnseigne, 
  updateEnseigne, 
  deleteEnseigne, 
  getEnseigneProjets, 
  getEnseignesProjetsMap,
  uploadEnseigneLogo,
  getEnseigneFournisseursDirectsConfig,
  setEnseigneFournisseurDirectOverride,
  deleteEnseigneFournisseurDirectOverride,
  deleteProjet,
  getEnseigneMagasins
} from '../services/api';
import { 
  Building2, 
  Plus, 
  Search, 
  Trash2, 
  ChevronRight,
  Phone,
  Mail,
  X,
  FolderOpen,
  Play,
  Clock,
  Pause,
  Edit2,
  Upload,
  Store,
  Image as ImageIcon,
  Euro,
  FileText,
  Truck,
  Check,
  RotateCcw,
  Archive,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import PrixEnseigneModal from '../components/PrixEnseigneModal';

const eur0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

/* Chiffre MAAG HT (devis signés) par magasin et par année, pour une enseigne dépliée */
const MagasinsCA = ({ enseigneId }) => {
  const [data, setData] = useState(null);
  const [annee, setAnnee] = useState('all');
  useEffect(() => {
    getEnseigneMagasins(enseigneId).then((r) => setData(r.data)).catch(() => setData({ magasins: [] }));
  }, [enseigneId]);
  if (!data) return <div className="text-xs text-slate-400 mb-4" data-testid="magasins-ca-loading">Chiffre par magasin…</div>;
  if (data.magasins.length === 0) return null;
  const annees = [...new Set(data.magasins.flatMap((m) => Object.keys(m.ca_par_annee)))].sort().reverse();
  const caMag = (m) => (annee === 'all' ? m.ca_total : (m.ca_par_annee[annee] || 0));
  const nbMag = (m) => (annee === 'all' ? m.nb_projets : m.projets.filter((p) => p.annee === annee).length);
  const total = data.magasins.reduce((s, m) => s + caMag(m), 0);
  return (
    <div className="mb-4 bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="magasins-ca" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-slate-900 text-white">
        <div className="flex items-center gap-2 text-sm font-semibold"><Store className="w-4 h-4" /> Chiffre par magasin <span className="text-xs font-normal text-slate-300">(CA MAAG HT, devis signés)</span></div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setAnnee('all')} className={`px-2.5 py-1 rounded-md text-xs font-medium ${annee === 'all' ? 'bg-white text-slate-900' : 'bg-slate-700 hover:bg-slate-600'}`} data-testid="magasins-ca-annee-all">Toutes</button>
          {annees.map((a) => <button key={a} type="button" onClick={() => setAnnee(a)} className={`px-2.5 py-1 rounded-md text-xs font-medium ${annee === a ? 'bg-white text-slate-900' : 'bg-slate-700 hover:bg-slate-600'}`} data-testid={`magasins-ca-annee-${a}`}>{a}</button>)}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr><th className="text-left px-4 py-2">Magasin</th><th className="text-right px-4 py-2">Projets</th><th className="text-right px-4 py-2">CA HT</th></tr>
        </thead>
        <tbody>
          {data.magasins.map((m) => (
            <tr key={m.key} className="border-t border-slate-100" data-testid={`magasin-ca-row-${m.key.split('|')[1].replace(/\s+/g, '_')}`}>
              <td className="px-4 py-2">
                <Link to={`/projets/${m.dernier_projet_id}`} className="font-medium text-slate-900 hover:text-blue-600">{m.ville}</Link>
                {m.adresse && <div className="text-xs text-slate-400 truncate max-w-xs">{m.adresse}</div>}
              </td>
              <td className="px-4 py-2 text-right text-slate-600">{nbMag(m)}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{eur0(caMag(m))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr><td className="px-4 py-2 font-semibold">Total enseigne {annee !== 'all' ? annee : ''}</td><td></td><td className="px-4 py-2 text-right font-mono font-bold" data-testid="magasins-ca-total">{eur0(total)}</td></tr>
        </tfoot>
      </table>
    </div>
  );
};

const ClientsPage = () => {
  const navigate = useNavigate();
  const [enseignes, setEnseignes] = useState([]);
  const [enseigneProjets, setEnseigneProjets] = useState({});
  const [loading, setLoading] = useState(true);
  const [projetsLoading, setProjetsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEnseigne, setEditingEnseigne] = useState(null);
  const [searchParams] = useSearchParams();
  const [expandedEnseigne, setExpandedEnseigne] = useState(searchParams.get('enseigne') || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [prixModalEnseigne, setPrixModalEnseigne] = useState(null);
  const logoInputRef = useRef(null);
  const [newEnseigne, setNewEnseigne] = useState({
    nom: '',
    adresse_siege: '',
    contact_principal: '',
    telephone: '',
    email: '',
    logo_url: ''
  });
  
  // Fournisseurs Directs modal
  const [fdModalEnseigne, setFdModalEnseigne] = useState(null);
  const [fdConfig, setFdConfig] = useState([]);
  const [loadingFD, setLoadingFD] = useState(false);
  const [editingFD, setEditingFD] = useState(null);
  const [editingFDValue, setEditingFDValue] = useState('');

  useEffect(() => {
    loadEnseignes();
  }, []);

  // Retour depuis un projet : ouvrir le dossier de l'enseigne et s'y positionner
  useEffect(() => {
    const target = searchParams.get('enseigne');
    if (!target || loading) return;
    const el = document.querySelector(`[data-testid="enseigne-card-${target}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, searchParams]);

  const loadEnseignes = async () => {
    try {
      const res = await getEnseignes();
      setEnseignes(res.data);
      setLoading(false);
      // Un seul appel pour tous les projets (financials calculés en parallèle côté serveur)
      try {
        const mapRes = await getEnseignesProjetsMap();
        setEnseigneProjets(mapRes.data || {});
      } catch (err) {
        // Fallback : chargement par enseigne, en parallèle
        const entries = await Promise.all(res.data.map(async (e) => {
          try { const r = await getEnseigneProjets(e.id); return [e.id, r.data]; } catch (_) { return [e.id, []]; }
        }));
        setEnseigneProjets(Object.fromEntries(entries));
      }
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setProjetsLoading(false);
    }
  };

  const handleCreateEnseigne = async (e) => {
    e.preventDefault();
    try {
      await createEnseigne(newEnseigne);
      toast.success('Enseigne créée');
      setShowModal(false);
      setNewEnseigne({ nom: '', adresse_siege: '', contact_principal: '', telephone: '', email: '', logo_url: '' });
      loadEnseignes();
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateEnseigne = async (e) => {
    e.preventDefault();
    try {
      await updateEnseigne(editingEnseigne.id, newEnseigne);
      toast.success('Enseigne mise à jour');
      setShowModal(false);
      setEditingEnseigne(null);
      setNewEnseigne({ nom: '', adresse_siege: '', contact_principal: '', telephone: '', email: '', logo_url: '' });
      loadEnseignes();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openEditModal = (enseigne) => {
    setEditingEnseigne(enseigne);
    setNewEnseigne({
      nom: enseigne.nom || '',
      adresse_siege: enseigne.adresse_siege || '',
      contact_principal: enseigne.contact_principal || '',
      telephone: enseigne.telephone || '',
      email: enseigne.email || '',
      logo_url: enseigne.logo_url || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingEnseigne(null);
    setNewEnseigne({ nom: '', adresse_siege: '', contact_principal: '', telephone: '', email: '', logo_url: '' });
    setShowModal(true);
  };

  const handleLogoUpload = async (e, enseigneId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const res = await uploadEnseigneLogo(enseigneId, file);
      toast.success('Logo uploadé avec succès');
      setNewEnseigne(prev => ({ ...prev, logo_url: res.data.logo_url }));
      loadEnseignes();
    } catch (err) {
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteEnseigne = async (id, nom) => {
    if (!window.confirm(`Supprimer l'enseigne "${nom}" et tous ses projets ?`)) return;
    try {
      await deleteEnseigne(id);
      toast.success('Enseigne supprimée');
      loadEnseignes();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteProjet = async (e, projetId, projetNom) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le projet "${projetNom}" ?\n\nCette action supprimera définitivement :\n- Tous les devis\n- Toutes les factures\n- Tous les achats artisans\n- Tous les plannings\n\nCette action est irréversible !`)) return;
    
    try {
      await deleteProjet(projetId);
      toast.success('Projet supprimé');
      // Refresh enseignes to update project list
      loadEnseignes();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Fournisseurs Directs functions
  const openFDModal = async (enseigne) => {
    setFdModalEnseigne(enseigne);
    setLoadingFD(true);
    try {
      const res = await getEnseigneFournisseursDirectsConfig(enseigne.id);
      setFdConfig(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoadingFD(false);
    }
  };
  
  const closeFDModal = () => {
    setFdModalEnseigne(null);
    setFdConfig([]);
    setEditingFD(null);
  };
  
  const handleSaveFDOverride = async (fd) => {
    if (!editingFDValue.trim()) {
      toast.error('La désignation ne peut pas être vide');
      return;
    }
    try {
      await setEnseigneFournisseurDirectOverride(fdModalEnseigne.id, {
        base_fd_id: fd.id,
        designation: editingFDValue.trim(),
        actif: true
      });
      toast.success('Personnalisation enregistrée');
      setEditingFD(null);
      // Reload
      const res = await getEnseigneFournisseursDirectsConfig(fdModalEnseigne.id);
      setFdConfig(res.data);
    } catch (e) {
      toast.error('Erreur');
    }
  };
  
  const handleResetFDToBase = async (fd) => {
    if (!fd.override_id) return;
    if (!window.confirm('Revenir à la désignation de base ?')) return;
    try {
      await deleteEnseigneFournisseurDirectOverride(fdModalEnseigne.id, fd.override_id);
      toast.success('Réinitialisé à la base');
      const res = await getEnseigneFournisseursDirectsConfig(fdModalEnseigne.id);
      setFdConfig(res.data);
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const toggleExpand = (id) => {
    setExpandedEnseigne(expandedEnseigne === id ? null : id);
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value || 0);
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_cours':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"><Play className="w-3 h-3 mr-1" />En cours</span>;
      case 'en_preparation':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />Préparation</span>;
      case 'stand_by':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"><Pause className="w-3 h-3 mr-1" />Stand-by</span>;
      case 'termine_facturation':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"><FileText className="w-3 h-3 mr-1" />Terminé – facturation</span>;
      case 'cloture':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-white"><CheckCircle className="w-3 h-3 mr-1" />Clôturé</span>;
      default:
        return null;
    }
  };

  const getMargeColor = (pct) => {
    if (pct === null || pct === undefined) return 'text-slate-400';
    if (pct < 15) return 'text-red-600';
    if (pct < 20) return 'text-orange-500';
    return 'text-green-600';
  };

  const filteredEnseignes = enseignes.filter(e => 
    e.nom.toLowerCase().includes(search.toLowerCase()) ||
    e.contact_principal?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="enseignes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Enseignes</h1>
          <p className="text-slate-500 mt-1">{enseignes.length} enseigne(s)</p>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="add-enseigne-btn"
          className="flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle enseigne
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            data-testid="search-input"
            type="text"
            placeholder="Rechercher une enseigne..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-96 h-10 pl-10 pr-4 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
      </div>

      {/* Enseignes list */}
      {filteredEnseignes.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            {search ? 'Aucune enseigne trouvée' : 'Aucune enseigne enregistrée'}
          </p>
          {!search && (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center mt-4 text-slate-900 hover:text-slate-700 font-medium"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter votre première enseigne
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEnseignes.map((enseigne) => {
            const projets = enseigneProjets[enseigne.id] || [];
            const currentYear = new Date().getFullYear();
            const projetsActifs = projets.filter(p => ['en_cours', 'en_preparation', 'preparation'].includes(p.statut));
            // CA = uniquement projets En cours / En préparation / Terminé facturation
            //      + projets clôturés (réalisés) de l'année en cours
            // Exclus du CA : Stand-by, Non réalisé (mais conservés pour stats dans Archives)
            const CA_STATUTS_ACTIFS = ['en_cours', 'en_preparation', 'preparation', 'termine_facturation'];
            const projetsForCA = projets.filter(p =>
              CA_STATUTS_ACTIFS.includes(p.statut) ||
              (p.statut === 'cloture' && p.annee_cloture === currentYear)
            );
            const isExpanded = expandedEnseigne === enseigne.id;
            const totalCA = projetsForCA.reduce((sum, p) => sum + (p.ca_maag || 0), 0);
            // Marge enseigne : uniquement les projets qui ont du CA (devis signé). Un chantier sans CA
            // (non obtenu, en attente) a des coûts estimés mais pas de marge mesurable → exclu.
            const totalMarge = projetsForCA.filter(p => (p.ca_maag || 0) > 0).reduce((sum, p) => sum + (p.marge_euros || 0), 0);
            const margePct = totalCA > 0 ? (totalMarge / totalCA * 100) : null;

            return (
              <div 
                key={enseigne.id} 
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`enseigne-card-${enseigne.id}`}
              >
                {/* Enseigne Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleExpand(enseigne.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mr-4 overflow-hidden border border-slate-200">
                        {enseigne.logo_url ? (
                          <img 
                            src={enseigne.logo_url} 
                            alt={enseigne.nom} 
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900">{enseigne.nom}</h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          {enseigne.contact_principal && (
                            <span>{enseigne.contact_principal}</span>
                          )}
                          {enseigne.telephone && (
                            <span className="flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {enseigne.telephone}
                            </span>
                          )}
                          {enseigne.email && (
                            <span className="flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {enseigne.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 mr-4">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Projets actifs</p>
                        <p className="text-lg font-bold text-slate-900">{projetsActifs.length}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">CA MAAG</p>
                        <p className="text-lg font-bold text-slate-900">{formatMoney(totalCA)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Marge</p>
                        <p className={`text-lg font-bold ${getMargeColor(margePct)}`}>
                          {margePct !== null ? `${margePct.toFixed(1)}%` : '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/enseignes/${enseigne.id}/devis-types`); }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Devis Types"
                        data-testid={`devis-types-btn-${enseigne.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setPrixModalEnseigne(enseigne); }}
                        className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Gérer les prix"
                        data-testid={`prix-btn-${enseigne.id}`}
                      >
                        <Euro className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openFDModal(enseigne); }}
                        className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Fournisseurs Directs"
                      >
                        <Truck className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditModal(enseigne); }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteEnseigne(enseigne.id, enseigne.nom); }}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </div>

                {/* Projets List (expanded) */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    <div className="p-4">
                      {/* Séparer projets actifs et archives */}
                      {(() => {
                        const projetsActifs = projets.filter(p => p.statut !== 'cloture' && p.statut !== 'projet_non_realise');
                        const projetsArchives = projets.filter(p => p.statut === 'cloture' || p.statut === 'projet_non_realise');
                        
                        return (
                          <>
                            <MagasinsCA enseigneId={enseigne.id} />
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                                Projets ({projetsLoading ? '…' : projets.length})
                              </h4>
                              <Link
                                to={`/projets/nouveau?enseigne=${enseigne.id}`}
                                className="flex items-center px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Nouveau projet
                              </Link>
                            </div>
                            
                            {projets.length === 0 ? (
                              <div className="text-center py-8 bg-white rounded-lg border border-dashed border-slate-300">
                                <FolderOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm text-slate-500">Aucun projet pour cette enseigne</p>
                                <Link
                                  to={`/projets/nouveau?enseigne=${enseigne.id}`}
                                  className="inline-flex items-center mt-2 text-sm text-slate-900 font-medium hover:text-slate-700"
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Créer un projet
                                </Link>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {/* Projets Actifs */}
                                {projetsActifs.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Play className="w-4 h-4 text-green-600" />
                                      <span className="text-sm font-medium text-slate-700">
                                        En cours ({projetsActifs.length})
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {projetsActifs.map((projet) => (
                                        <div
                                          key={projet.id}
                                          className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                                        >
                                          <Link
                                            to={`/projets/${projet.id}`}
                                            className="flex items-center gap-4 flex-1"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <FolderOpen className="w-5 h-5 text-slate-400" />
                                            <div>
                                              <p className="font-medium text-slate-900">{projet.ville}</p>
                                              {projet.adresse && (
                                                <p className="text-sm text-slate-500">{projet.adresse}</p>
                                              )}
                                            </div>
                                            {getStatutBadge(projet.statut)}
                                          </Link>
                                          <div className="flex items-center gap-6">
                                            <div className="text-right">
                                              <p className="text-xs text-slate-500">CA</p>
                                              <p className="font-mono text-sm font-medium">{formatMoney(projet.ca_maag)}</p>
                                            </div>
                                            <div className="text-right">
                                              <p className="text-xs text-slate-500">Marge</p>
                                              <p className={`font-mono text-sm font-bold ${getMargeColor(projet.marge_pourcentage)}`}>
                                                {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
                                              </p>
                                            </div>
                                            <button
                                              onClick={(e) => handleDeleteProjet(e, projet.id, `${projet.ville}`)}
                                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                              title="Supprimer le projet"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                            <Link
                                              to={`/projets/${projet.id}`}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <ChevronRight className="w-4 h-4 text-slate-400" />
                                            </Link>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Projets Archives */}
                                {projetsArchives.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <Archive className="w-4 h-4 text-slate-500" />
                                      <span className="text-sm font-medium text-slate-500">
                                        Clôturés ({projetsArchives.length})
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {projetsArchives.map((projet) => (
                                        <div
                                          key={projet.id}
                                          className="flex items-center justify-between p-4 bg-slate-100 rounded-lg border border-slate-200 hover:border-slate-300 transition-all opacity-75 hover:opacity-100"
                                        >
                                          <Link
                                            to={`/projets/${projet.id}`}
                                            className="flex items-center gap-4 flex-1"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <FolderOpen className="w-5 h-5 text-slate-400" />
                                            <div>
                                              <p className="font-medium text-slate-700">{projet.ville}</p>
                                              {projet.adresse && (
                                                <p className="text-sm text-slate-500">{projet.adresse}</p>
                                              )}
                                            </div>
                                            {getStatutBadge(projet.statut)}
                                          </Link>
                                          <div className="flex items-center gap-6">
                                            <div className="text-right">
                                              <p className="text-xs text-slate-500">CA</p>
                                              <p className="font-mono text-sm font-medium text-slate-600">{formatMoney(projet.ca_maag)}</p>
                                            </div>
                                            <div className="text-right">
                                              <p className="text-xs text-slate-500">Marge</p>
                                              <p className={`font-mono text-sm font-bold ${getMargeColor(projet.marge_pourcentage)}`}>
                                                {projet.marge_pourcentage !== null ? `${projet.marge_pourcentage.toFixed(1)}%` : '-'}
                                              </p>
                                            </div>
                                            <button
                                              onClick={(e) => handleDeleteProjet(e, projet.id, `${projet.ville}`)}
                                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                              title="Supprimer le projet"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                            <Link
                                              to={`/projets/${projet.id}`}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <ChevronRight className="w-4 h-4 text-slate-400" />
                                            </Link>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Message si aucun projet actif */}
                                {projetsActifs.length === 0 && projetsArchives.length > 0 && (
                                  <div className="text-center py-4 bg-white rounded-lg border border-dashed border-slate-300 mb-4">
                                    <p className="text-sm text-slate-500">Tous les projets sont clôturés</p>
                                    <Link
                                      to={`/projets/nouveau?enseigne=${enseigne.id}`}
                                      className="inline-flex items-center mt-2 text-sm text-slate-900 font-medium hover:text-slate-700"
                                    >
                                      <Plus className="w-3 h-3 mr-1" /> Créer un nouveau projet
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" data-testid="enseigne-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingEnseigne ? 'Modifier l\'enseigne' : 'Nouvelle enseigne'}
              </h2>
              <button 
                onClick={() => { setShowModal(false); setEditingEnseigne(null); }}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={editingEnseigne ? handleUpdateEnseigne : handleCreateEnseigne} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nom de l'enseigne *
                </label>
                <input
                  data-testid="enseigne-nom-input"
                  type="text"
                  value={newEnseigne.nom}
                  onChange={(e) => setNewEnseigne({...newEnseigne, nom: e.target.value})}
                  placeholder="Ex: ADOPT, PITAYA, KIABI..."
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Adresse du siège
                </label>
                <input
                  type="text"
                  value={newEnseigne.adresse_siege}
                  onChange={(e) => setNewEnseigne({...newEnseigne, adresse_siege: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact principal
                </label>
                <input
                  type="text"
                  value={newEnseigne.contact_principal}
                  onChange={(e) => setNewEnseigne({...newEnseigne, contact_principal: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    value={newEnseigne.telephone}
                    onChange={(e) => setNewEnseigne({...newEnseigne, telephone: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newEnseigne.email}
                    onChange={(e) => setNewEnseigne({...newEnseigne, email: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>

              {/* Logo upload - only for editing mode */}
              {editingEnseigne && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Logo de l'enseigne
                  </label>
                  <div className="flex items-center gap-4">
                    {/* Logo preview */}
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                      {newEnseigne.logo_url ? (
                        <img 
                          src={newEnseigne.logo_url} 
                          alt="Logo" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-slate-300" />
                      )}
                    </div>
                    <div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, editingEnseigne.id)}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                      >
                        {uploadingLogo ? (
                          <>Chargement...</>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {newEnseigne.logo_url ? 'Changer le logo' : 'Uploader un logo'}
                          </>
                        )}
                      </button>
                      <p className="text-xs text-slate-500 mt-1">
                        PNG, JPG ou SVG (max 2MB)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingEnseigne(null); }}
                  className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  data-testid="save-enseigne-btn"
                  className="flex-1 h-10 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
                >
                  {editingEnseigne ? 'Enregistrer' : 'Créer l\'enseigne'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Prix Enseigne Modal */}
      {prixModalEnseigne && (
        <PrixEnseigneModal 
          enseigne={prixModalEnseigne} 
          onClose={() => setPrixModalEnseigne(null)} 
        />
      )}
      
      {/* Fournisseurs Directs Modal */}
      {fdModalEnseigne && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center">
                <Truck className="w-5 h-5 text-purple-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Fournisseurs Directs</h2>
                  <p className="text-sm text-slate-500">Personnalisation pour <strong>{fdModalEnseigne.nom}</strong></p>
                </div>
              </div>
              <button
                onClick={closeFDModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingFD ? (
                <div className="text-center py-12 text-slate-500">Chargement...</div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-slate-500 mb-4">
                    Modifiez les désignations pour cette enseigne. Les modifications n'affecteront que les nouveaux projets de <strong>{fdModalEnseigne.nom}</strong>.
                  </p>
                  
                  {fdConfig.map((fd) => (
                    <div key={fd.id} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      fd.has_override ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50 hover:bg-slate-100'
                    }`}>
                      <div className="flex-1">
                        {editingFD === fd.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editingFDValue}
                              onChange={(e) => setEditingFDValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveFDOverride(fd);
                                if (e.key === 'Escape') setEditingFD(null);
                              }}
                              className="flex-1 h-9 px-3 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveFDOverride(fd)}
                              className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingFD(null)}
                              className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <span className={`text-sm ${fd.has_override ? 'font-medium text-purple-800' : 'text-slate-700'}`}>
                              {fd.designation}
                            </span>
                            {fd.has_override && (
                              <span className="ml-2 text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded">
                                Personnalisé
                              </span>
                            )}
                            {fd.has_override && fd.base_designation !== fd.designation && (
                              <span className="ml-2 text-xs text-slate-400 truncate max-w-[150px]" title={fd.base_designation}>
                                (base: {fd.base_designation})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {editingFD !== fd.id && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingFD(fd.id);
                              setEditingFDValue(fd.designation);
                            }}
                            className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="Personnaliser"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {fd.has_override && (
                            <button
                              onClick={() => handleResetFDToBase(fd)}
                              className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg"
                              title="Revenir à la base"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={closeFDModal}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
